/**
 * Prepare a local receipt blob for capture and multipart upload.
 * iOS often stores HEIC (or blank-typed) Blobs in IndexedDB; wrapping as a
 * real File and JPEG-re-encoding when possible avoids broken multipart bodies.
 *
 * PNG/WebP screenshots are accepted as-is when small; large ones are
 * re-encoded to JPEG. PDF invoices are stored as-is (Claude reads PDFs
 * natively; Xero attaches the original). Anything still over the 8MB
 * capture cap (headroom under Xero's 10MB attach limit) is compressed on
 * a quality/edge ladder for photos, then rejected — at capture time, not
 * after a successful upload that later fails to attach.
 */
export const MAX_RECEIPT_BYTES = 8 * 1024 * 1024;
export const XERO_ATTACHMENT_CAP_MB = 10;
export const RECEIPT_CAPTURE_CAP_MB = 8;

const LARGE_SCREENSHOT_BYTES = 1.5 * 1024 * 1024;

const COMPRESS_LADDER: ReadonlyArray<{ maxEdge: number; quality: number }> = [
  { maxEdge: 2400, quality: 0.85 },
  { maxEdge: 2400, quality: 0.72 },
  { maxEdge: 2000, quality: 0.65 },
  { maxEdge: 1600, quality: 0.58 },
  { maxEdge: 1280, quality: 0.5 },
  { maxEdge: 1024, quality: 0.45 },
];

const IMAGE_MIME = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
]);

export class ReceiptTooLargeError extends Error {
  readonly byteSize: number;

  constructor(byteSize: number, kind: 'photo' | 'file' = 'photo') {
    const mb = (byteSize / (1024 * 1024)).toFixed(1);
    const noun = kind === 'file' ? 'file' : 'photo';
    super(
      `This ${noun} is ${mb}MB. Keep it under ${RECEIPT_CAPTURE_CAP_MB}MB (Xero’s attach limit is ${XERO_ATTACHMENT_CAP_MB}MB) — use a smaller file, a tighter crop, or a lower-resolution export.`,
    );
    this.name = 'ReceiptTooLargeError';
    this.byteSize = byteSize;
  }
}

export class ReceiptUnsupportedTypeError extends Error {
  readonly mimeType: string;

  constructor(mimeType: string, filename = '') {
    const label = filename.trim() || mimeType || 'this file';
    super(
      `“${label}” is not a supported receipt. Use a PDF invoice, or a JPEG, PNG, WebP or HEIC photo.`,
    );
    this.name = 'ReceiptUnsupportedTypeError';
    this.mimeType = mimeType;
  }
}

function isPdfDeclared(declared: string, filename: string): boolean {
  return declared === 'application/pdf' || /\.pdf$/i.test(filename);
}

async function blobStartsWithPdf(blob: Blob): Promise<boolean> {
  const head = new Uint8Array(await blob.slice(0, 5).arrayBuffer());
  return (
    head.length >= 4 &&
    head[0] === 0x25 &&
    head[1] === 0x50 &&
    head[2] === 0x44 &&
    head[3] === 0x46
  );
}

function pdfFileName(filename: string): string {
  const base = filename.replace(/^.*[/\\]/, '').trim();
  if (base && /\.pdf$/i.test(base)) return base;
  return 'invoice.pdf';
}

export async function prepareReceiptUploadFile(
  blob: Blob,
  opts: { mimeType?: string; filename?: string } = {},
): Promise<File> {
  const declared = (opts.mimeType || blob.type || '').toLowerCase();
  const filename = opts.filename ?? '';

  const pdf =
    isPdfDeclared(declared, filename) || (await blobStartsWithPdf(blob));
  if (pdf) {
    if (blob.size > MAX_RECEIPT_BYTES) {
      throw new ReceiptTooLargeError(blob.size, 'file');
    }
    return new File([blob], pdfFileName(filename), {
      type: 'application/pdf',
      lastModified: Date.now(),
    });
  }

  if (
    declared &&
    declared !== 'application/octet-stream' &&
    !IMAGE_MIME.has(declared)
  ) {
    throw new ReceiptUnsupportedTypeError(declared, filename);
  }

  const isPngOrWebp =
    declared === 'image/png' || declared === 'image/webp';
  const wantsJpeg =
    !declared ||
    declared === 'application/octet-stream' ||
    declared === 'image/heic' ||
    declared === 'image/heif' ||
    declared.endsWith('.heic') ||
    (isPngOrWebp && blob.size > LARGE_SCREENSHOT_BYTES);

  let outBlob = blob;
  let outMime = declared || 'image/jpeg';
  let outName = opts.filename ?? 'receipt.jpg';

  if (wantsJpeg) {
    const jpeg = await tryReencodeJpeg(blob);
    if (jpeg) {
      outBlob = jpeg;
      outMime = 'image/jpeg';
      outName = outName.replace(/\.(heic|heif|png|webp)$/i, '.jpg');
      if (!/\.jpe?g$/i.test(outName)) outName = 'receipt.jpg';
    } else if (declared === 'image/heic' || declared === 'image/heif') {
      outMime = declared;
      outName = opts.filename ?? 'receipt.heic';
    } else if (isPngOrWebp) {
      // Keep original when canvas re-encode fails (still valid server-side).
      outMime = declared;
      outName =
        opts.filename ??
        (declared === 'image/webp' ? 'receipt.webp' : 'receipt.png');
    } else if (!declared || declared === 'application/octet-stream') {
      outMime = 'image/jpeg';
      outName = 'receipt.jpg';
    }
  } else if (declared === 'image/png') {
    outName = opts.filename ?? 'receipt.png';
    outMime = 'image/png';
  } else if (declared === 'image/webp') {
    outName = opts.filename ?? 'receipt.webp';
    outMime = 'image/webp';
  } else {
    outMime = declared === 'image/jpg' ? 'image/jpeg' : declared;
    outName = opts.filename ?? 'receipt.jpg';
  }

  if (outBlob.size > MAX_RECEIPT_BYTES) {
    const compressed = await compressUnderCap(blob);
    if (compressed) {
      outBlob = compressed;
      outMime = 'image/jpeg';
      outName = outName.replace(/\.[a-z0-9]+$/i, '.jpg');
      if (!/\.jpe?g$/i.test(outName)) outName = 'receipt.jpg';
    }
  }

  if (outBlob.size > MAX_RECEIPT_BYTES) {
    throw new ReceiptTooLargeError(outBlob.size, 'photo');
  }

  return new File([outBlob], outName, {
    type: outMime,
    lastModified: Date.now(),
  });
}

async function compressUnderCap(source: Blob): Promise<Blob | null> {
  for (const step of COMPRESS_LADDER) {
    const jpeg = await tryReencodeJpeg(source, step);
    if (jpeg && jpeg.size > 0 && jpeg.size <= MAX_RECEIPT_BYTES) {
      return jpeg;
    }
  }
  return null;
}

function tryReencodeJpeg(
  blob: Blob,
  opts: { maxEdge?: number; quality?: number } = {},
): Promise<Blob | null> {
  const maxEdge = opts.maxEdge ?? 2400;
  const quality = opts.quality ?? 0.85;
  return new Promise((resolve) => {
    if (typeof createImageBitmap !== 'function' && typeof Image === 'undefined') {
      resolve(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        if (width > maxEdge || height > maxEdge) {
          const scale = maxEdge / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          URL.revokeObjectURL(url);
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (b) => {
            URL.revokeObjectURL(url);
            resolve(b && b.size > 0 ? b : null);
          },
          'image/jpeg',
          quality,
        );
      } catch {
        URL.revokeObjectURL(url);
        resolve(null);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}
